/**
 * @lastAudited 2025-01-19
 * @auditStatus PASS
 * @auditNotes Pre-publish checklist passed. No issues found.
 */
import { useSEOHead } from '../../../hooks/useSEOHead';
import Breadcrumb from '../../../components/feature/Breadcrumb';
import { ServiceAreasHero } from '../components/ServiceAreasHero';
import { NearbyAreasSection } from '../components/NearbyAreasSection';
import { useEffect } from 'react';
import { injectJsonLd, removeJsonLd } from '../../../lib/schemaBuilder';

export default function WarrentonServiceAreaPage() {
  useSEOHead({
    title: 'Used Cars Warrenton Oregon — Your Hometown Dealer | Rockhound Auto',
    description: 'Quality used cars in Warrenton, OR. Locally owned, coastal-tested vehicles, honest pricing. Serving Warrenton and the Oregon Coast.',
    keywords: 'used cars Warrenton Oregon, Warrenton car dealer, cars in Warrenton OR, used vehicles Warrenton',
    canonicalUrl: 'https://rockhoundauto.com/service-areas/warrenton',
  });

  const nearbyAreas = [
    { name: 'Astoria', slug: 'astoria' },
    { name: 'Seaside', slug: 'seaside' },
    { name: 'Cannon Beach', slug: 'cannon-beach' },
    { name: 'Gearhart', slug: 'gearhart' },
    { name: 'Long Beach WA', slug: 'long-beach-wa' },
    { name: 'Ilwaco WA', slug: 'ilwaco' },
  ];

  useEffect(() => {
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://rockhoundauto.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Service Areas',
          item: 'https://rockhoundauto.com/service-areas',
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: 'Warrenton',
          item: 'https://rockhoundauto.com/service-areas/warrenton',
        },
      ],
    };

    injectJsonLd('schema-warrenton-breadcrumb', breadcrumbSchema);

    return () => {
      removeJsonLd('schema-warrenton-breadcrumb');
    };
  }, []);

  return (
    <>
      <Breadcrumb
        items={[
          { label: 'Service Areas', path: '/service-areas' },
          { label: 'Warrenton, Oregon' },
        ]}
      />

      <ServiceAreasHero
        city="Warrenton"
        state="Oregon"
        description="We're right here in Warrenton — not some out-of-town dealer trying to dump inventory on coast folks. Every vehicle is inspected for coastal durability, because we know what the salt air, fog, and wind do to cars that aren't properly maintained."
        distance="Located in Warrenton"
      />

      <section aria-label="Why Warrenton residents trust Rockhound Auto" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Your Hometown Used Car Dealer
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              We're not just in Warrenton — we're from Warrenton. We shop at the same stores, drive the same roads, and deal with the same weather. When we inspect a car, we're thinking about whether we'd sell it to our neighbor. Because we probably will.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-home-heart-line text-3xl text-teal-700"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Locally Owned &amp; Operated</h3>
              <p className="text-slate-700">
                We're not a franchise or a corporate chain. We're a small business that lives and works in Warrenton. Our reputation is everything.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-3xl text-teal-700"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Coastal-Tested Vehicles</h3>
              <p className="text-slate-700">
                Every vehicle gets a full undercarriage inspection. We check for rust, seal integrity, and electrical issues caused by salt air and moisture.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-price-tag-3-line text-3xl text-teal-700"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Honest Pricing</h3>
              <p className="text-slate-700">
                No dealer markup, no mystery fees, no pressure. The price you see online is the price you pay. We're not trying to hit a monthly quota.
              </p>
            </div>
          </div>

          <div className="bg-slate-50 rounded-xl p-8">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Why Warrenton Residents Trust Us</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Because we're your neighbors. We see you at the grocery store, at the gas station, at the post office. If we sell you a lemon, we'll hear about it — probably while standing in line at the bank.
            </p>
            <p className="text-slate-700 leading-relaxed mb-4">
              That's why every car we sell is inspected like we're selling it to family. We check for the things that matter on the coast: rust, seal integrity, electrical issues from moisture, and whether it'll actually start on a foggy morning in February.
            </p>
            <p className="text-slate-700 leading-relaxed">
              We're not trying to be the biggest dealer on the coast. We're trying to be the most honest one. And in a small town like Warrenton, that's the only way to survive.
            </p>
          </div>
        </div>
      </section>

      <NearbyAreasSection cityName="Warrenton" nearbyAreas={nearbyAreas} />
    </>
  );
}

export { WarrentonServiceAreaPage };